import React, { useEffect, useRef } from "react"

import useTracking from "hooks/useTracking"
import { useFeaturedVideos } from "store/contexts/featured-videos"
import { addItcContent } from "../utils/addLinkTracking"

import { Container } from "./styles"

import Banner from "./banner"
import Recirculation from "./recirculation"
import TextBlurb from "./text-blurb"
import TextWithMedia from "./text-with-media"
import SignupForm from "./signup-form"
import ProductShoppable from "./product-shoppable"
import MultiProductShoppable from "./multiproduct-shoppable"
import Video from "./video"

const PromotionalPlacement = ({ article, element }) => {
  const { id, absoluteUrl, category } = article
  const { promotionalUnit, primaryBanner, secondaryBanner, showBorder, trackingId, internalId } =
    element
  const type = promotionalUnit?.internal.type

  addItcContent(promotionalUnit, id)
  const html = promotionalUnit?.customHtml?.childMarkdownRemark?.html

  const { setShowFeaturedVideos } = useFeaturedVideos()

  if (html) {
    const replaceVariables = {
      mbg_article_id: id,
      mbg_article_url: absoluteUrl,
    }

    promotionalUnit.customHtml.childMarkdownRemark.html = html.replace(
      /{{(.*?)}}/g,
      (match, token) => (token in replaceVariables ? replaceVariables[token] : match),
    )
  }

  const { track, trackView, trackClick } = useTracking()
  const ref = useRef(null)

  const extraProps = {
    position: element.layoutPosition,
    creative: type,
    promotion_id: trackingId,
    name: internalId,
  }

  trackView(ref, null, extraProps, (props) => track("Promotion Viewed", props))
  if (!(promotionalUnit?.button || promotionalUnit?.secondButton)) {
    trackClick(ref, null, extraProps, (props) => track("Promotion Clicked", props))
  }

  if (promotionalUnit?.button) {
    const { tracking } = promotionalUnit.button
    element.promotionalUnit.button.trackingOverride = {
      ...(tracking.includes("Click") && {
        Click: { trackingId: null, event: "Promotion Clicked", props: extraProps },
      }),
    }
  }

  if (promotionalUnit?.secondButton) {
    const { tracking } = promotionalUnit.secondButton
    element.promotionalUnit.secondButton.trackingOverride = {
      ...(tracking.includes("Click") && {
        Click: { trackingId: null, event: "Promotion Clicked", props: extraProps },
      }),
    }
  }

  useEffect(() => {
    // When we have a promotional placement that uses a text blurb with Connatix Player custom code
    // or a video, we want to hide featured videos in the bottom section
    if (type === "ContentfulTextBlurb") {
      setShowFeaturedVideos(!/(connatix|cnx)/i.test(html))
    }

    if (type === "ContentfulVideo") {
      setShowFeaturedVideos(false)
    }
  }, [type, html, setShowFeaturedVideos])

  const renderPlacement = () => {
    let Component = null

    switch (type) {
      case "ContentfulRecirculation":
        Component = <Recirculation elRef={ref} category={category} items={promotionalUnit?.items} />
        break
      case "ContentfulTextWithMedia":
        Component = promotionalUnit?.product ? (
          <ProductShoppable elRef={ref} element={element} />
        ) : (
          <TextWithMedia elRef={ref} element={element} isPromotionalPlacement />
        )
        break
      case "ContentfulSignupForm":
        Component = <SignupForm elRef={ref} element={element} isPromotionalPlacement />
        break
      case "ContentfulTextBlurb":
        Component = <TextBlurb elRef={ref} element={promotionalUnit} />
        break
      case "ContentfulGridLayout":
        Component = <MultiProductShoppable elRef={ref} component={promotionalUnit} />
        break
      case "ContentfulVideo":
        Component = <Video element={promotionalUnit} elRef={ref} />
        break
      default:
        Component = null
    }

    return Component
  }

  return (
    <Container showBorder={showBorder}>
      {primaryBanner && <Banner banner={primaryBanner} isPrimary />}
      {secondaryBanner && <Banner banner={secondaryBanner} />}
      {renderPlacement()}
    </Container>
  )
}

export default PromotionalPlacement
